import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import axiosInstance from "../api/axiosInstance";

export default function HowToGuidePage() {
  const [siteSettings, setSiteSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSiteSettings = async () => {
      try {
        const response = await axiosInstance.get('/api/v1/site-settings/');
        setSiteSettings(response.data);
      } catch (error) {
        // Handle error silently
      } finally {
        setLoading(false);
      }
    };
    
    fetchSiteSettings();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <svg className="animate-spin h-8 w-8 text-customIndigo mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-gray-600">Loading guide...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">How to Use ResidentPass</h1>
          <p className="mt-2 text-lg text-gray-600">
            Your complete guide to managing visitor permits with our reliable system
          </p>
        </div>

        {/* Main Content */}
        <div className="space-y-6">
          {/* Getting Started Section */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center mb-4">
              <span className="text-blue-600 bg-blue-50 p-3 rounded-lg">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </span>
              <h2 className="ml-3 text-xl font-semibold text-gray-900">Getting Started</h2>
            </div>
            <div className="space-y-4">
              {/* <p className="text-gray-700">
                Welcome to ResidentPass! Our system ensures reliable permit management 
                even when external services are temporarily unavailable. Every permit is processed in the background 
                with automatic retries and comprehensive status tracking.
              </p> */}
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-700">
                      <strong>Pro Tip:</strong> Start by exploring your Dashboard to understand your permit limits 
                      and current usage. Our system processes permits in the background, so you'll see real-time status updates.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Dashboard Section */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center mb-4">
              <span className="text-green-600 bg-green-50 p-3 rounded-lg">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </span>
              <h2 className="ml-3 text-xl font-semibold text-gray-900">Dashboard Overview</h2>
            </div>
            <div className="space-y-4">
              <p className="text-gray-700">
                Your Dashboard provides real-time insights into your permit status with our system:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                    <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </span>
                  <span className="text-gray-700">
                    <strong>Active Permits:</strong> Permits currently active in the external system
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                    <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </span>
                  <span className="text-gray-700">
                    <strong>Queued Permits:</strong> Permits waiting to be processed by the external system
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                    <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </span>
                  <span className="text-gray-700">
                    <strong>Usage Progress:</strong> Visual indicator showing your permit usage
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Adding Permits Section */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center mb-4">
              <span className="text-purple-600 bg-purple-50 p-3 rounded-lg">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </span>
              <h2 className="ml-3 text-xl font-semibold text-gray-900">Adding New Permits</h2>
            </div>
            <div className="space-y-4">
              {/* <p className="text-gray-700">
                Create permits with our reliable background system. All permits are processed in the background 
                with automatic retries and comprehensive error handling:
              </p> */}
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-2">Step-by-Step Process:</h3>
                  <ol className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start">
                      <span className="flex-shrink-0 w-5 h-5 bg-customIndigo text-white rounded-full flex items-center justify-center mr-2 mt-0.5 text-xs font-medium">1</span>
                      <span>Navigate to "Add Permit" from the main menu</span>
                    </li>
                    {siteSettings?.can_add_reference && (
                      <li className="flex items-start">
                        <span className="flex-shrink-0 w-5 h-5 bg-customIndigo text-white rounded-full flex items-center justify-center mr-2 mt-0.5 text-xs font-medium">2</span>
                        <span>Fill in the reference label (e.g. "Bay 1")</span>
                      </li>
                    )}
                    <li className="flex items-start">
                      <span className="flex-shrink-0 w-5 h-5 bg-customIndigo text-white rounded-full flex items-center justify-center mr-2 mt-0.5 text-xs font-medium">
                        {siteSettings?.can_add_reference ? "3" : "2"}
                      </span>
                      <span>Enter vehicle details: registration number, make, model, and colour</span>
                    </li>
                    <li className="flex items-start">
                      <span className="flex-shrink-0 w-5 h-5 bg-customIndigo text-white rounded-full flex items-center justify-center mr-2 mt-0.5 text-xs font-medium">
                        {siteSettings?.can_add_reference ? "4" : "3"}
                      </span>
                      <span>Add driver information: first name and last name</span>
                    </li>
                     {(siteSettings?.default_end_date_limit_hours === 0) && (
                       <li className="flex items-start">
                         <span className="flex-shrink-0 w-5 h-5 bg-customIndigo text-white rounded-full flex items-center justify-center mr-2 mt-0.5 text-xs font-medium">
                           {siteSettings?.can_add_reference ? "5" : "4"}
                         </span>
                         <span>Set permit duration: choose between specific end date or indefinite</span>
                       </li>
                     )}
                     {(siteSettings?.default_end_date_limit_hours > 0) && (
                       <li className="flex items-start">
                         <span className="flex-shrink-0 w-5 h-5 bg-customIndigo text-white rounded-full flex items-center justify-center mr-2 mt-0.5 text-xs font-medium">
                           {siteSettings?.can_add_reference ? "5" : "4"}
                         </span>
                         <span>Permit duration will be automatically set to {siteSettings.default_end_date_limit_hours} hours from start time</span>
                       </li>
                     )}
                    {siteSettings?.can_schedule_permits && (
                      <li className="flex items-start">
                        <span className="flex-shrink-0 w-5 h-5 bg-customIndigo text-white rounded-full flex items-center justify-center mr-2 mt-0.5 text-xs font-medium">
                          {siteSettings?.can_add_reference ? "6" : "5"}
                        </span>
                        <span>Optionally schedule the permit for future activation (at least 1 minute in advance)</span>
                      </li>
                    )}
                    <li className="flex items-start">
                      <span className="flex-shrink-0 w-5 h-5 bg-customIndigo text-white rounded-full flex items-center justify-center mr-2 mt-0.5 text-xs font-medium">
                        {siteSettings?.can_add_reference 
                          ? (siteSettings?.can_schedule_permits ? "7" : "6")
                          : (siteSettings?.can_schedule_permits ? "6" : "5")
                        }
                      </span>
                      <span>Review and submit the permit (processed in the background)</span>
                    </li>
                  </ol>
                </div>
                
                {/* Technical Requirements Section */}
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                  <h3 className="font-medium text-gray-900 mb-2">System Features:</h3>
                  <div className="space-y-2 text-sm text-gray-700">
                    <div className="flex items-start">
                      <span className="text-blue-500 mr-2 mt-1">•</span>
                      <span><strong>Background Processing:</strong> All permits are queued and processed in the background with automatic retries</span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-blue-500 mr-2 mt-1">•</span>
                      <span><strong>Duplicate Prevention:</strong> System prevents duplicate permits for the same vehicle and time period</span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-blue-500 mr-2 mt-1">•</span>
                      <span><strong>Automatic Retries:</strong> Failed operations are automatically retried with exponential backoff</span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-blue-500 mr-2 mt-1">•</span>
                      <span><strong>Real-time Status:</strong> Track permit status from creation through to completion</span>
                    </div>
                  </div>
                </div>

                {/* <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700">
                        <strong>Important:</strong> Our background system ensures your permits are processed reliably. 
                        Even if external services are temporarily unavailable, your permits will be queued and processed automatically.
                      </p>
                    </div>
                  </div>
                </div> */}
              </div>
            </div>
          </div>

          {/* Permit Status & Management Section */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center mb-4">
              <span className="text-orange-600 bg-orange-50 p-3 rounded-lg">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </span>
              <h2 className="ml-3 text-xl font-semibold text-gray-900">Permit Status & Management</h2>
            </div>
            <div className="space-y-4">
              <p className="text-gray-700">
                Understanding permit statuses and management features in our system:
              </p>
              
              {/* Status Types */}
              <div className="space-y-3">
                <h3 className="font-medium text-gray-900">Permit Status Types:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  {siteSettings?.can_schedule_permits && (
                    <div className="flex items-center">
                      <span className="w-3 h-3 bg-blue-500 rounded-full mr-2"></span>
                      <span className="text-sm font-medium">Scheduled</span>
                    </div>
                  )}
                  {siteSettings?.can_schedule_permits && (
                    <p className="text-xs text-gray-600 ml-5">Future-dated permits waiting to activate</p>
                  )}
                    
                    <div className="flex items-center">
                      <span className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></span>
                      <span className="text-sm font-medium">Queued</span>
                    </div>
                    <p className="text-xs text-gray-600 ml-5">Waiting to be processed by external system</p>
                    
                    <div className="flex items-center">
                      <span className="w-3 h-3 bg-green-500 rounded-full mr-2"></span>
                      <span className="text-sm font-medium">Active</span>
                    </div>
                    <p className="text-xs text-gray-600 ml-5">Successfully active in external system</p>
                    
                    <div className="flex items-center">
                      <span className="w-3 h-3 bg-orange-500 rounded-full mr-2"></span>
                      <span className="text-sm font-medium">Update Pending</span>
                    </div>
                    <p className="text-xs text-gray-600 ml-5">End date update being processed</p>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <span className="w-3 h-3 bg-red-500 rounded-full mr-2"></span>
                      <span className="text-sm font-medium">Failed</span>
                    </div>
                    <p className="text-xs text-gray-600 ml-5">Creation failed after retries</p>
                    
                    <div className="flex items-center">
                      <span className="w-3 h-3 bg-red-500 rounded-full mr-2"></span>
                      <span className="text-sm font-medium">Failed Limit</span>
                    </div>
                    <p className="text-xs text-gray-600 ml-5">Failed due to permit limit exceeded</p>
                    
                    <div className="flex items-center">
                      <span className="w-3 h-3 bg-gray-500 rounded-full mr-2"></span>
                      <span className="text-sm font-medium">Cancelled</span>
                    </div>
                    <p className="text-xs text-gray-600 ml-5">Successfully cancelled</p>
                    
                    <div className="flex items-center">
                      <span className="w-3 h-3 bg-gray-500 rounded-full mr-2"></span>
                      <span className="text-sm font-medium">Expired</span>
                    </div>
                    <p className="text-xs text-gray-600 ml-5">Automatically expired by system</p>
                  </div>
                </div>
              </div>

              {/* Management Features */}
              <div className="space-y-3">
                <h3 className="font-medium text-gray-900">Management Features:</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  {siteSettings?.can_schedule_permits && (
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2 mt-1">•</span>
                      <span><strong>Manual Activation:</strong> Activate scheduled permits immediately using "Set Live Now"</span>
                    </li>
                  )}
                  {siteSettings?.can_schedule_permits && (
                    <li className="flex items-start">
                      <span className="text-blue-500 mr-2 mt-1">•</span>
                      <span><strong>Update Permits:</strong> {siteSettings?.default_end_date_limit_hours === 0 ? 'Modify end dates or reschedule permits' : 'Reschedule permits'}</span>
                    </li>
                  )}
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2 mt-1">•</span>
                    <span><strong>Cancel Permits:</strong> Cancel any active, queued{siteSettings?.can_schedule_permits ? ', or scheduled' : ''} permits</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-500 mr-2 mt-1">•</span>
                    <span><strong>Real-time Updates:</strong> See live updates as permits are processed</span>
                  </li>
                </ul>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-700">
                      <strong>Pro Tip:</strong> {siteSettings?.can_schedule_permits ? 'Use the "Set Live Now" feature to activate scheduled permits immediately when you need them sooner than planned.' : 'Monitor your permit status in real-time as they are processed in the background.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Managing Permits Section */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center mb-4">
              <span className="text-indigo-600 bg-indigo-50 p-3 rounded-lg">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              </span>
              <h2 className="ml-3 text-xl font-semibold text-gray-900">Managing Your Permits</h2>
            </div>
            <div className="space-y-4">
              <p className="text-gray-700">
                The "My Permits" page provides comprehensive permit management with advanced filtering and real-time status tracking:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <h3 className="font-medium text-gray-900">Filtering Options:</h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start">
                      <span className="text-blue-500 mr-2">•</span>
                      <span><strong>Status:</strong> Filter by any permit status ({siteSettings?.can_schedule_permits ? 'Scheduled, ' : ''}Queued, Active, Failed, etc.)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-500 mr-2">•</span>
                      <span><strong>Date Range:</strong> Filter by permit creation date range</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-500 mr-2">•</span>
                      <span><strong>Search:</strong> Find permits by vehicle details, driver name, or reference</span>
                    </li>
                  </ul>
                </div>
                <div className="space-y-3">
                  <h3 className="font-medium text-gray-900">Permit Actions:</h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    {siteSettings?.can_schedule_permits && (
                      <li className="flex items-start">
                        <span className="text-green-500 mr-2">•</span>
                        <span><strong>Set Live Now:</strong> Activate scheduled permits immediately</span>
                      </li>
                    )}
                    {siteSettings?.can_schedule_permits && (
                      <li className="flex items-start">
                        <span className="text-blue-500 mr-2">•</span>
                        <span><strong>Update:</strong> {siteSettings?.default_end_date_limit_hours === 0 ? 'Modify end dates or reschedule permits' : 'Reschedule permits'}</span>
                      </li>
                    )}
                    <li className="flex items-start">
                      <span className="text-red-500 mr-2">•</span>
                      <span><strong>Cancel:</strong> Cancel permits in any status</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-purple-500 mr-2">•</span>
                      <span><strong>Real-time Status:</strong> See live updates as permits are processed</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Account Settings Section */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center mb-4">
              <span className="text-gray-600 bg-gray-50 p-3 rounded-lg">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </span>
              <h2 className="ml-3 text-xl font-semibold text-gray-900">Account Settings</h2>
            </div>
            <div className="space-y-4">
              <p className="text-gray-700">
                Manage your account settings and security preferences:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                    <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </span>
                  <span className="text-gray-700">
                    <strong>Change Password:</strong> Update your account password for enhanced security
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                    <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </span>
                  <span className="text-gray-700">
                    <strong>Contact Support:</strong> Get personalised help from our support team via email
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                    <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </span>
                  <span className="text-gray-700">
                    <strong>Logout:</strong> Securely sign out from your account when finished
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Troubleshooting Section */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center mb-4">
              <span className="text-red-600 bg-red-50 p-3 rounded-lg">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </span>
              <h2 className="ml-3 text-xl font-semibold text-gray-900">Troubleshooting</h2>
            </div>
            <div className="space-y-4">
              {/* <p className="text-gray-700">
                Common issues and their solutions with our background system:
              </p> */}
              <div className="space-y-3">
                <div className="border-l-4 border-red-400 pl-4">
                  <h3 className="font-medium text-gray-900">Permit Creation Failed</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Check that the vehicle registration and time period combination is unique, 
                    and that you haven't reached your permit limit. Failed permits will be automatically retried.
                  </p>
                </div>
                <div className="border-l-4 border-orange-400 pl-4">
                  <h3 className="font-medium text-gray-900">Permit Stuck in Queued Status</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    This is normal! Our system processes permits in the background. Queued permits will be 
                    processed automatically with retries. Check back in a few minutes for status updates.
                  </p>
                </div>
                {siteSettings?.can_schedule_permits && (
                  <div className="border-l-4 border-blue-400 pl-4">
                    <h3 className="font-medium text-gray-900">Scheduled Permit Not Activating</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Scheduled permits activate automatically, but you can use "Set Live Now" to activate 
                      them immediately. Ensure the scheduled time is at least 1 minute in the future.
                    </p>
                  </div>
                )}
                <div className="border-l-4 border-purple-400 pl-4">
                  <h3 className="font-medium text-gray-900">Can't Find a Permit</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Use the search and filter options in "My Permits" to locate specific permits by 
                    vehicle details, driver name, or reference.
                  </p>
                </div>
                <div className="border-l-4 border-green-400 pl-4">
                  <h3 className="font-medium text-gray-900">Permit Status Not Updating</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Our system updates statuses in real-time as permits are processed. If a status seems stuck, 
                    try refreshing the page. All operations are logged and tracked automatically.
                  </p>
                </div>
              </div>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      <strong>Need More Help?</strong> If you're experiencing issues not covered here, 
                      contact our support team through the Settings page for personalised assistance.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 pt-4">
            <Link
              to="/add-permit"
              className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-customIndigo hover:bg-[rgb(45,100,112)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-customIndigo transition-colors duration-200"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Add New Permit
            </Link>
            <Link
              to="/my-permits"
              className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-customIndigo transition-colors duration-200"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
              View My Permits
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
} 